import React, { useState, useEffect, useMemo } from "react";
import DatePicker, { TimePicker } from "../../../../components/inputs/MomentDatePicker";
import { Table, Button, Tag, Modal, Input, Select, message, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "react-query";
import {
  useCreatePermissionRequest,
  useGetAllPermissions,
} from "../../../../services/permissions.service";
import { useGetSchoolLocation } from "../../../../services/geofencing.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const Permissions = () => {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [requestType, setRequestType] = useState("check_in");
  const [schoolTimings, setSchoolTimings] = useState(null);

  const [formData, setFormData] = useState({
    permissionFor: "check_in",
    date: null,
    fromTime: null,
    toTime: null,
    reason: "",
  });

  const params = useMemo(
    () => ({
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "permission",
    }),
    [dateRange],
  );

  const {
    data: permissionsData,
    isLoading,
    refetch,
  } = useGetAllPermissions({}, params);

  const { data: schoolLocationData } = useGetSchoolLocation();

  useEffect(() => {
    if (schoolLocationData) {
      setSchoolTimings({
        checkInTime: schoolLocationData.check_in_time,
        checkOutTime: schoolLocationData.check_out_time,
      });

      if (requestType === "check_in") {
        const schoolCheckIn = moment(schoolLocationData.check_in_time, "HH:mm");
        setFormData((prev) => ({ ...prev, fromTime: schoolCheckIn }));
      } else if (requestType === "check_out") {
        const schoolCheckOut = moment(
          schoolLocationData.check_out_time,
          "HH:mm",
        );
        setFormData((prev) => ({ ...prev, toTime: schoolCheckOut }));
      }
    }
  }, [schoolLocationData, requestType]);

  const data = useMemo(() => {
    return permissionsData || [];
  }, [permissionsData]);

  const createPermission = useCreatePermissionRequest({
    onSuccess: (response) => {
      message.success("Permission request submitted successfully");
      queryClient.invalidateQueries("allPermissions");
      handleCancel();
      refetch();
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.detail || "Failed to submit request",
      );
    },
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "green";
      case "pending":
        return "orange";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const getPermissionTypeColor = (permissionFor) => {
    switch (permissionFor) {
      case "check_in":
        return "blue";
      case "check_out":
        return "purple";
      case "flex":
        return "cyan";
      default:
        return "default";
    }
  };

  const getPermissionTypeText = (permissionFor) => {
    switch (permissionFor) {
      case "check_in":
        return "Late Check-in";
      case "check_out":
        return "Early Check-out";
      case "flex":
        return "Flex";
      default:
        return "Permission";
    }
  };

  const handleDateRangeChange = (dates) => setDateRange(dates);

  const showModal = () => setIsModalVisible(true);

  const handleCancel = () => {
    setIsModalVisible(false);
    setRequestType("check_in");
    const initialFromTime = schoolTimings
      ? moment(schoolTimings.checkInTime, "HH:mm")
      : null;
    setFormData({
      permissionFor: "check_in",
      date: null,
      fromTime: initialFromTime,
      toTime: null,
      reason: "",
    });
  };

  const onPermissionTypeChange = (value) => {
    setRequestType(value);
    let newFromTime = null;
    let newToTime = null;

    if (schoolTimings) {
      if (value === "check_in") {
        newFromTime = moment(schoolTimings.checkInTime, "HH:mm");
      } else if (value === "check_out") {
        newToTime = moment(schoolTimings.checkOutTime, "HH:mm");
      }
    }

    setFormData((prev) => ({
      ...prev,
      permissionFor: value,
      fromTime: newFromTime,
      toTime: newToTime,
    }));
  };

  const handleSave = () => {
    const { permissionFor, date, fromTime, toTime, reason } = formData;
    if (!permissionFor || !date || !fromTime || !toTime || !reason) {
      message.error("Please fill all required fields");
      return;
    }

    const selectedDate = date.format("YYYY-MM-DD");

    const dateConflict = data?.find(
      (r) => r.status !== "rejected" && r.date === selectedDate,
    );
    if (dateConflict) {
      message.warning(
        `You already have a ${dateConflict.status} permission request for this date.`,
      );
      return;
    }
    const pendingConflict = data?.find((r) => r.status === "pending");
    if (pendingConflict) {
      message.warning(
        "You already have a pending permission request. Please wait for it to be reviewed.",
      );
      return;
    }

    const requestData = {
      type: "permission",
      reason: reason,
      date: selectedDate,
      leave_duration: "flex",
      permission_for: permissionFor === "flex" ? "both" : permissionFor,
      from_time: `${selectedDate}T${fromTime.format("HH:mm:ss")}+05:30`,
      to_time: `${selectedDate}T${toTime.format("HH:mm:ss")}+05:30`,
    };

    createPermission.mutate(requestData);
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "from_time",
      key: "date",
      align: "center",
      render: (text, record) => (
        <div className="font-semibold">
          {text
            ? moment(text).format("DD/MM/YYYY")
            : moment(record.created_at).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      title: "Permission Type",
      key: "permissionType",
      align: "center",
      render: (_, record) => (
        <Tag
          color={getPermissionTypeColor(record.permission_for)}
          className="capitalize"
        >
          {getPermissionTypeText(record.permission_for)}
        </Tag>
      ),
    },
    {
      title: "Permission Time",
      key: "permissionTime",
      align: "center",
      render: (_, record) => {
        const colorClass =
          record.permission_for === "check_in"
            ? "text-green-600"
            : record.permission_for === "check_out"
              ? "text-red-600"
              : "text-blue-600";

        return (
          <div className="flex flex-col">
            <span className={`${colorClass} font-bold`}>
              From:{" "}
              {record.from_time
                ? moment(record.from_time).format("hh:mm A")
                : "-"}
            </span>
            <span className={`${colorClass} font-bold`}>
              To:{" "}
              {record.to_time ? moment(record.to_time).format("hh:mm A") : "-"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      align: "center",
      render: (text) => <span className="text-gray-600">{text || "-"}</span>,
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, record) => (
        <Tag
          color={getStatusColor(record.status)}
          className="capitalize px-3 py-1 font-bold"
        >
          {record.status === "approved"
            ? "Approved"
            : record.status === "rejected"
              ? "Rejected"
              : "Pending"}
        </Tag>
      ),
    },
  ];

  return (
    <div className="mt-8 min-h-screen">
      <div className="bg-white drop-shadow-md p-8 rounded-lg shadow-2xl border border-gray-100">
        <div className="text-2xl text-orange-500 font-semibold py-4">
          Staff Attendance - Permissions
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-base font-bold text-gray-700">
              Select Date Range:
            </span>
            <RangePicker
              format="DD/MM/YYYY"
              value={dateRange}
              onChange={handleDateRangeChange}
              style={{ width: 300 }}
            />
          </div>
          <Button
            type="primary"
            onClick={showModal}
            className="bg-blue-600 hover:bg-blue-700 font-semibold px-8 py-2 h-10 rounded-xl shadow-lg"
          >
            Request Permission
          </Button>
        </div>

        <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-md">
          <Table
            columns={columns}
            dataSource={data?.length > 0 ? data : []}
            loading={isLoading}
            rowKey="_id"
            rowClassName="hover:bg-blue-50 transition-colors duration-200"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </div>
      </div>

      <Modal
        title={
          <div className="text-xl font-bold text-gray-800">
            Request Permission
          </div>
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSave}
            loading={createPermission.isLoading}
            className="bg-blue-600"
          >
            Submit Request
          </Button>,
        ]}
        width={600}
        closeIcon={<CloseOutlined />}
        className="rounded-2xl"
      >
        <div className="flex flex-col gap-4 mt-4">
          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Permission Type
            </label>
            <Select
              value={formData.permissionFor}
              onChange={onPermissionTypeChange}
              style={{ width: "100%" }}
            >
              <Option value="check_in">Late Check-in Permission</Option>
              <Option value="check_out">Early Check-out Permission</Option>
              <Option value="flex">Flex Permission</Option>
            </Select>
          </div>

          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Date
            </label>
            <DatePicker
              value={formData.date}
              onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              disabledDate={(current) => current && current < moment().startOf("day")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                {requestType === "check_in"
                  ? "From Time (School Start)"
                  : requestType === "check_out"
                    ? "When you want to check out"
                    : "When you want to go out"}
              </label>
              <TimePicker
                value={formData.fromTime}
                onChange={(time) =>
                  setFormData((prev) => ({ ...prev, fromTime: time }))
                }
                disabled={requestType === "check_in" && !!schoolTimings}
                style={{ width: "100%" }}
                format="h:mm a"
                use12Hours
              />
            </div>
            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                {requestType === "check_in"
                  ? "When you want to check in"
                  : requestType === "check_out"
                    ? "To Time (School Ends)"
                    : "Returning"}
              </label>
              <TimePicker
                value={formData.toTime}
                onChange={(time) =>
                  setFormData((prev) => ({ ...prev, toTime: time }))
                }
                disabled={requestType === "check_out" && !!schoolTimings}
                style={{ width: "100%" }}
                format="h:mm a"
                use12Hours
              />
            </div>
          </div>

          {schoolTimings && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800 font-bold">
                School Timings: {schoolTimings.checkInTime} to{" "}
                {schoolTimings.checkOutTime}
              </div>
              <div className="text-xs text-blue-600 mt-1">
                {requestType === "check_in"
                  ? `Note: From time is automatically set to school start time (${schoolTimings.checkInTime}).`
                  : requestType === "check_out"
                    ? `Note: To time is automatically set to school end time (${schoolTimings.checkOutTime}).`
                    : "Note: Flex permission allows you to specify both from and to times"}
              </div>
            </div>
          )}

          <div>
            <label className="block mb-2 font-semibold text-gray-700">
              Reason
            </label>
            <TextArea
              rows={4}
              value={formData.reason}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="Enter reason for permission"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Permissions;
